/**
 * ZAdminAttributionBackfill — Zod schemas for the deployment-attribution backfill.
 *
 * Single source of truth for the domain's API validation: these schemas generate
 * both runtime validation (Zod) and compile-time types (TApi).
 *
 * The backfill fills `deploymentId` / `deploymentSessionId` /
 * `deploymentAttribution` on the five analytics fact tables for rows written
 * before those columns existed. It is admin-triggered rather than a script,
 * because Porter deployments have no CLI access.
 *
 * Admin endpoints are protected by PBAC (Policy-Based Access Control).
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

/**
 * The fact tables, in the order the sweep must visit them.
 *
 * Mirrors the `AttributionBackfillTable` database enum. Kept as a `const` array
 * so the frontend can iterate it without importing the Prisma runtime.
 */
export const ATTRIBUTION_BACKFILL_TABLES = [
  "CHAT",
  "MESSAGE",
  "WORKFLOW_EXECUTION",
  "WORKFLOW_EXECUTION_NODE",
  "SCORE"
] as const;

export const AttributionBackfillTableSchema = z.enum(ATTRIBUTION_BACKFILL_TABLES);

export const ATTRIBUTION_BACKFILL_STATUSES = ["IDLE", "RUNNING", "COMPLETED", "FAILED"] as const;

export const AttributionBackfillStatusSchema = z.enum(ATTRIBUTION_BACKFILL_STATUSES);

/** One table's run record, paired with the work still outstanding. */
export const AttributionBackfillTableStatusSchema = z.object({
  /** Which fact table this row describes. */
  factTable: AttributionBackfillTableSchema,
  /** Where its sweep stands. */
  status: AttributionBackfillStatusSchema,
  /** Human-readable derivation, e.g. "Message ← Chat". */
  derivation: z.string(),
  /**
   * Rows a full run would still attribute, counted live against the same
   * predicate the sweep uses — never a stored copy, which would go stale.
   *
   * `-1` means the count itself failed. It is deliberately not `0`, because `0`
   * is a meaningful answer ("nothing left to do") and reporting a failed count
   * as convergence would be a false green.
   */
  remaining: z.number().int(),
  /** Cumulative rows moved from UNRESOLVED to ATTRIBUTED across all runs. */
  rowsAttributed: z.number().int().nonnegative(),
  /** Cumulative batches executed across all runs. */
  batches: z.number().int().nonnegative(),
  startedAt: z.string().datetime().nullable(),
  finishedAt: z.string().datetime().nullable(),
  /** Error that moved this table to FAILED. Cleared when a run restarts it. */
  lastError: z.string().nullable(),
  /**
   * Stored `RUNNING` while nothing is actually in flight — an interrupted run.
   *
   * A pod eviction or OOM kill stops a worker before it can write `FAILED`, so
   * the stored status says RUNNING forever. This is the difference between that
   * and a live sweep, and it has to be a separate fact: reading the stored flag
   * as liveness is what made the admin surface refuse the resume the backfill
   * exists to support.
   */
  isStale: z.boolean(),
  /**
   * Stored `COMPLETED` while `remaining` is still above zero.
   *
   * `COMPLETED` records that a PAST run converged — it is not a live claim. A
   * release that adds a derivation makes rows countable that were not countable
   * when that run ended, and the stored word does not move. The pair then reads
   * as a contradiction, and the word is what an operator reads first.
   *
   * A different fact from `isStale`, and the two are mutually exclusive: that
   * one is RUNNING with nothing in flight, this one is COMPLETED with work
   * left. A `-1` remaining is a FAILED COUNT and is never superseded — reading
   * a count outage as outstanding work would raise a false alarm on every one.
   */
  isSuperseded: z.boolean(),
  updatedAt: z.string().datetime()
});

export const AttributionBackfillStatusResponseSchema = z.object({
  tables: z.array(AttributionBackfillTableStatusSchema),
  /** Sum of `remaining` across tables, with failed counts excluded rather than summed as 0. */
  totalRemaining: z.number().int().nonnegative(),
  isRunning: z.boolean()
});

/**
 * `.default({})` so an absent payload parses to an empty object.
 *
 * The controller passes `body` straight through — `nexus/require-body-validation`
 * matches the literal `.parse(body)`, so a `body ?? {}` at the call site reads to
 * the linter as no validation at all. Putting the tolerance in the schema keeps
 * the validated form the obvious one.
 */
export const AttributionBackfillRunBodySchema = z
  .object({
    /**
     * Rows written per batched statement. Clamped server-side to [100, 50000];
     * omitted means the default (10000).
     */
    batchSize: z.number().int().positive().optional()
  })
  .default({});

export const AttributionBackfillRunResponseSchema = z.object({
  /** False when a sweep is already active — the request is refused, not queued. */
  started: z.boolean(),
  /** The clamped batch size the run will actually use. */
  batchSize: z.number().int().positive(),
  message: z.string()
});

export const ZAdminAttributionBackfill = {
  /**
   * Run record and outstanding work for every fact table.
   * GET /api/admin/attribution-backfill/status
   */
  Status: {
    method: "GET" as const,
    path: "/api/admin/attribution-backfill/status",
    Response: ApiSuccessSchema(AttributionBackfillStatusResponseSchema)
  },

  /**
   * Start a sweep. Returns immediately; the run continues on the queue.
   * POST /api/admin/attribution-backfill/run
   */
  Run: {
    method: "POST" as const,
    path: "/api/admin/attribution-backfill/run",
    Body: AttributionBackfillRunBodySchema,
    Response: ApiSuccessSchema(AttributionBackfillRunResponseSchema)
  }
} as const;
